// DeliveryManagement.tsx
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeliveryFormSchema } from 'pages/GenericOrderDetailsPage/types/delivery';
import { Button } from '@/components/ui/button';
import { DeliveryTable } from 'pages/GenericOrderDetailsPage/components/DeliveryTable';
import { Customers, DeliveryInfo, Product } from 'pages/GenericOrderDetailsPage/types/order';
import { useParams } from 'react-router-dom';
import { Spinner } from 'components';
import { useMutation, useQueryClient } from 'react-query';
import { addDeliveryAddress, updateDeliveryAddress } from 'pages/GenericOrderDetailsPage/apis';
import { toast } from 'sonner';
import { DeliveryModal } from 'pages/GenericOrder/steps/delivery-modal';

export const Delivery = (
    { deliveriesData, deliveryButtonState, editButtonState, customers, products:ProductList , milestone}: {
        deliveriesData: DeliveryInfo[];
        deliveryButtonState: {
            disabled: boolean;
            visible: boolean;
        },
        customers: Customers;
        products: Product[];
        milestone:any;
        editButtonState: boolean
    }
) => {
    const queryClient = useQueryClient();
    const id = useParams().id || '';

    // check order reject or not
    const finalStatus = milestone?.filter((item:any)=> item?.stage === "Order Rejected" && item?.status === "Cancelled")

    const createDeliveryMutation = useMutation({
        mutationFn: ({ data }: { data: DeliveryFormSchema[] }) => addDeliveryAddress(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
            toast.success("Delivery created successfully");
            setFormOpen(false);
        },
        onError: () => {
            toast.error("Error creating delivery");
        },
    });
     const updateDeliveryMutation = useMutation({
        mutationFn: ({ data }: { data: DeliveryFormSchema}) => updateDeliveryAddress(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
            toast.success("Delivery updated successfully");
            setFormOpen(false);
        },
        onError: () => {
            toast.error("Error updating delivery");
        },
    });

    const [formOpen, setFormOpen] = useState(false);
    const [editingDelivery, setEditingDelivery] = useState<boolean>(false);
    const [defaultValues, setDefaultValues] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('All');
    const [selectedDeliveryGenericId, setSelectedDeliveryGenericId] = useState<string | null>(null);
    if (!deliveriesData) return <div>No data available</div>;

    const customersList = customers?.product_customer?.reduce((acc: { value: string; label: string; products: {id:string}[] }[], customerItem) => {
        if (customerItem.status === "Reject") return acc;

        const customer = customerItem.customer_detail?.full_name;
        if (customer && !acc.some(c => c.label === customer)) {
            acc.push({ value: customerItem.created_for?.toString(), label: customer, products: [{id:customerItem.id}] });
        }
        return acc;
    }, []);

    const products = customers?.product_customer?.reduce((acc: { label: string; value: string }[], productItem) => {
        const product = productItem?.name;
        if (product && !acc.some(p => p.label === product)) {
            acc.push({ value: productItem?.id, label: product });
        }
        return acc;
    }, []);

    let groupedDeliveryData = deliveriesData.reduce((acc: { [key: string]: DeliveryInfo[] }, delivery: DeliveryInfo) => {
        const customerName = delivery.full_name || 'All';
        if (!acc[customerName]) {
            acc[customerName] = [];
        }
        acc[customerName].push(delivery);
        return acc;
    }, {});

    const allDeliveries = Object.values(groupedDeliveryData).flat();
    groupedDeliveryData['All'] = allDeliveries;

    // put all at first
    const allDeliveriesData = groupedDeliveryData['All'];
    delete groupedDeliveryData['All'];
    groupedDeliveryData = {
        'All': allDeliveriesData,
        ...groupedDeliveryData
    };

    const tabKeys = Object.keys(groupedDeliveryData);

    const handleEdit = (id: string| number) => {
        setFormOpen(true);
        setEditingDelivery(true);
        const deliveryToEdit = allDeliveries.find(delivery => delivery.uuid === id);
        if (deliveryToEdit) {
            const address = deliveryToEdit.address_detail;
            const defaultValues = {
                deliveryId: deliveryToEdit.delivery_id,
                deliveryMode: { value: deliveryToEdit.delivery_mode, label: deliveryToEdit.delivery_mode },
                addresses: [{
                    address1: address?.street_number || '',
                    address2: address?.street_number || '',
                    city: address?.City || '',
                    state: { value: address?.State || '', label: address?.State || '' },
                    country: { value: address?.country || '', label: address?.country || '' },
                    landmark: address?.landmark || '',
                    pincode: address?.pincode || ''
                }],
                products: deliveryToEdit.product_name ? [{ value: deliveryToEdit.generic_order_product_id, label: deliveryToEdit.product_name }] : [],
                customers: { value: deliveryToEdit.deliver_to.toString(), label: deliveryToEdit.full_name, products: [{id:deliveryToEdit.generic_order_product_id}] },
                address_id: deliveryToEdit.address_id,                
            };  
            setDefaultValues(defaultValues); 
            setSelectedDeliveryGenericId(deliveryToEdit.uuid);         
        }        
    };

    const handleAddNew = () => {
        setFormOpen(true);
        setEditingDelivery(false);
        setDefaultValues(null)
    };

    const handleFormSubmit = (data:any) => {
        const dataToSend = {
            address1: data?.addresses?.[0]?.address1,
            address2: data?.addresses?.[0]?.address2,
            city: data?.addresses?.[0]?.city,
            country: data?.addresses?.[0]?.country?.label,
            landmark: data?.addresses?.[0]?.landmark,
            state: data?.addresses?.[0]?.state?.value,
            pincode: data?.addresses?.[0]?.pincode,
            delivery_channel: data?.delivery_channel,
            delivery_mode: data?.deliveryMode?.value,
            delivery_id: data?.deliveryId,
            products: data?.products?.map((product: { value: string }) => product.value),
            customers: data?.customers?.value,
            address_id: data?.address_id,
            uuid: selectedDeliveryGenericId || null
        }
        if(editingDelivery) {
            return updateDeliveryMutation.mutate({
                data: dataToSend
            });
        }
        else{
            createDeliveryMutation.mutate({ 
                data:[dataToSend]
            })       
        }
    };

    if (createDeliveryMutation.isLoading) return <Spinner />

    return (
        <div className="container mx-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="flex justify-between items-center mb-[18px]">
                    <TabsList className=" space-x-3">
                        {tabKeys.map(tabKey => (
                            <TabsTrigger className={`rounded-xl h-6 font-normal text-[13px] border py-1 px-4 ${activeTab === tabKey ? 'border-blue-700 text-blue-700' : 'border-[#E5E5E5] text-[#999999]'}`} key={tabKey} value={tabKey}>
                                {tabKey}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <Button disabled={deliveryButtonState?.disabled || finalStatus?.length > 0} onClick={handleAddNew} className='rounded-lg border border-blue-700 text-blue-700'>
                        Add Delivery
                        <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.96094 6.1224C3.96094 4.28145 5.45332 2.78906 7.29427 2.78906H11.9231C12.1594 2.78906 12.3845 2.88933 12.5425 3.06493L15.8304 6.71804C15.9681 6.87106 16.0443 7.06964 16.0443 7.27551V14.8724C16.0443 16.7133 14.5519 18.2057 12.7109 18.2057H7.29427C5.45332 18.2057 3.96094 16.7133 3.96094 14.8724V6.1224Z" fill="#165FE3" />
                            <path d="M16.0391 7.78906V7.27551C16.0391 7.06964 15.9629 6.87106 15.8251 6.71804L12.5373 3.06493C12.3793 2.88933 12.1541 2.78906 11.9179 2.78906H11.0391V4.45573C11.0391 6.29668 12.5315 7.78906 14.3724 7.78906H16.0391Z" fill="#98BDFF" />
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M12.9283 11.8692C12.6769 12.1058 12.2813 12.0937 12.0448 11.8423L10 9.6689L7.95517 11.8423C7.71867 12.0937 7.32317 12.1058 7.07175 11.8692C6.82034 11.6327 6.80825 11.2372 7.04484 10.9858L9.29225 8.59698C9.6785 8.18639 10.3215 8.18639 10.7078 8.59698L12.9552 10.9858C13.1917 11.2372 13.1797 11.6327 12.9283 11.8692Z" fill="white" />
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 15.7891C9.65483 15.7891 9.375 15.5092 9.375 15.1641V8.91406C9.375 8.5689 9.65483 8.28906 10 8.28906C10.3452 8.28906 10.625 8.5689 10.625 8.91406V15.1641C10.625 15.5092 10.3452 15.7891 10 15.7891Z" fill="white" />
                        </svg>

                    </Button>
                </div>

                {tabKeys.map(tabKey => (
                    <TabsContent key={tabKey} value={tabKey}>
                        <DeliveryTable
                            deliveries={groupedDeliveryData[tabKey]}
                            onEdit={handleEdit}
                            products={ProductList}
                            deliveryButtonState={editButtonState}
                            finalStatus={finalStatus}
                        />
                    </TabsContent>
                ))}
            </Tabs>
            <DeliveryModal
                isOpen={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}     
                initialData={editingDelivery ? defaultValues : null}           
                productOptions={products}
                customers={customersList}
            />
        </div>
    );
};